<script setup>
// Müşteri 360: kişi kartında Ticimax + Shopify sipariş geçmişi (ULTRA sürüm —
// ürün görselleri, kargo firması/takip no/link, adres, not, kapıda ödeme).
// Veri: /api/v1/accounts/:id/marketplace/customers/history?contact_id=X
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const route = useRoute();
const router = useRouter();
const loading = ref(false);
const error = ref('');
const data = ref(null);
const copiedId = ref(null);

const CHANNEL_BADGE = {
  ticimax: { label: 'Koza', cls: 'bg-violet-100 text-violet-800' },
  shopify: { label: 'ModaX', cls: 'bg-green-100 text-green-800' },
};

const fmtTl = v =>
  new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(v || 0);

const stats = computed(() => data.value?.stats || {});
const orders = computed(() => data.value?.orders || []);
const identities = computed(() => data.value?.identities || []);

const load = async () => {
  if (!props.contactId) return;
  loading.value = true;
  error.value = '';
  try {
    data.value = await fetchMarketplaceJson(
      route.params.accountId,
      `customers/history?contact_id=${props.contactId}`
    );
  } catch (e) {
    error.value = 'Sipariş geçmişi yüklenemedi';
  } finally {
    loading.value = false;
  }
};

watch(() => props.contactId, load, { immediate: true });

const copyTracking = async order => {
  if (!order.cargo?.trackingNo) return;
  try {
    await navigator.clipboard.writeText(order.cargo.trackingNo);
    copiedId.value = order.id;
    setTimeout(() => {
      copiedId.value = null;
    }, 1500);
  } catch (e) {
    // pano izni yoksa sessiz geç
  }
};

const statusBadge = order => {
  if (order.cargoUndelivered)
    return { text: 'Teslim edilemedi', cls: 'bg-ruby-100 text-ruby-800' };
  if (order.cargoDeliveredOn)
    return {
      text: `Teslim: ${order.cargoDeliveredOn}`,
      cls: 'bg-green-100 text-green-800',
    };
  const s = order.cargoStatus || order.status || '';
  return { text: s, cls: 'bg-slate-100 text-slate-700' };
};

const isCod = order => (order.paymentType || '').includes('Kapıda');

// İade/Değişim Merkezi'ni bu siparişle önceden doldurulmuş açar
const startReturn = order => {
  router.push({
    name: 'return_center',
    params: { accountId: route.params.accountId },
    query: { siparis: order.orderNumber },
  });
};
</script>

<template>
  <div class="px-6 pb-6">
    <div
      class="rounded-xl border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-3"
    >
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-n-slate-12">Sipariş Geçmişi</h3>
        <div class="flex gap-1">
          <span
            v-for="idn in identities"
            :key="idn.channel + idn.externalId"
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="CHANNEL_BADGE[idn.channel]?.cls"
          >
            {{ idn.channelLabel }}
          </span>
        </div>
      </div>

      <div v-if="loading" class="py-6 text-center text-n-slate-11 text-sm">
        Yükleniyor…
      </div>
      <div v-else-if="error" class="py-6 text-center text-ruby-600 text-sm">
        {{ error }}
      </div>
      <template v-else-if="data">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div class="rounded-lg bg-n-alpha-black2 p-3">
            <div class="text-xs text-n-slate-11">Toplam Sipariş</div>
            <div class="text-lg font-bold text-n-slate-12">
              {{ stats.totalOrders }}
            </div>
          </div>
          <div class="rounded-lg bg-n-alpha-black2 p-3">
            <div class="text-xs text-n-slate-11">Toplam Harcama</div>
            <div class="text-lg font-bold text-n-slate-12">
              {{ fmtTl(stats.totalSpent) }} ₺
            </div>
          </div>
          <div class="rounded-lg bg-n-alpha-black2 p-3">
            <div class="text-xs text-n-slate-11">Teslim / Teslimsiz</div>
            <div class="text-lg font-bold">
              <span class="text-green-700">{{ stats.delivered }}</span>
              <span class="text-n-slate-11"> / </span>
              <span class="text-ruby-600">{{ stats.undelivered }}</span>
            </div>
          </div>
          <div class="rounded-lg bg-n-alpha-black2 p-3">
            <div class="text-xs text-n-slate-11">Son Sipariş</div>
            <div class="text-lg font-bold text-n-slate-12">
              {{ stats.lastOrderAt || '—' }}
            </div>
          </div>
        </div>

        <div
          v-if="!orders.length"
          class="py-4 text-center text-n-slate-11 text-sm"
        >
          Bu kişiye bağlı sipariş bulunamadı.
        </div>

        <div v-else class="flex flex-col gap-3">
          <div
            v-for="o in orders"
            :key="o.id"
            class="rounded-xl border border-n-weak overflow-hidden"
          >
            <!-- Üst şerit: kanal, no, tarih, durum, tutar -->
            <div
              class="flex flex-wrap items-center gap-2 px-3 py-2 bg-n-alpha-black2"
            >
              <span
                class="px-1.5 py-0.5 rounded text-[11px] font-bold"
                :class="CHANNEL_BADGE[o.channel]?.cls"
              >
                {{ CHANNEL_BADGE[o.channel]?.label || o.channel }}
              </span>
              <span class="font-semibold text-sm text-n-slate-12">
                #{{ o.orderNumber }}
              </span>
              <span class="text-xs text-n-slate-11">{{ o.orderedAt }}</span>
              <span
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold"
                :class="statusBadge(o).cls"
              >
                {{ statusBadge(o).text }}
              </span>
              <span class="ml-auto font-bold text-sm text-n-slate-12">
                {{ fmtTl(o.amount) }} ₺
              </span>
              <button
                class="px-2 py-1 rounded-md bg-woot-75 text-woot-700 text-[11px] font-bold hover:bg-woot-100"
                title="Bu sipariş için iade/değişim kodu ver"
                @click.stop="startReturn(o)"
              >
                ↩️ İade/Değişim
              </button>
            </div>

            <!-- Ürünler: görselli -->
            <div class="flex flex-col divide-y divide-n-weak">
              <div
                v-for="(it, idx) in o.items"
                :key="idx"
                class="flex items-center gap-3 px-3 py-2"
              >
                <img
                  v-if="it.image"
                  :src="it.image"
                  class="w-12 h-14 rounded-md object-cover shrink-0 border border-n-weak"
                  loading="lazy"
                />
                <div
                  v-else
                  class="w-12 h-14 rounded-md bg-n-alpha-black2 shrink-0 flex items-center justify-center text-n-slate-10 text-lg"
                >
                  👗
                </div>
                <div class="min-w-0 flex-1">
                  <div class="text-sm text-n-slate-12 truncate">
                    {{ it.title || it.code }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ it.variant }}
                    <span v-if="it.code" class="font-mono ml-1">{{
                      it.code
                    }}</span>
                  </div>
                </div>
                <div class="text-sm text-n-slate-12 shrink-0 text-right">
                  <div>{{ it.quantity }} × {{ fmtTl(it.unitPrice) }} ₺</div>
                </div>
              </div>
            </div>

            <!-- Detay şeridi: ödeme, kargo, adres, not -->
            <div
              class="px-3 py-2 border-t border-n-weak flex flex-col gap-1.5 text-xs text-n-slate-11"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="px-1.5 py-0.5 rounded font-semibold"
                  :class="
                    isCod(o)
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-slate-100 text-slate-700'
                  "
                >
                  💳 {{ o.paymentType || 'Ödeme bilgisi yok' }}
                </span>
                <span v-if="o.shippingAmount" class="text-n-slate-11">
                  Kargo ücreti: {{ fmtTl(o.shippingAmount) }} ₺
                </span>
              </div>

              <div
                v-if="o.cargo?.company || o.cargo?.trackingNo"
                class="flex flex-wrap items-center gap-2"
              >
                <span>🚚 {{ o.cargo.company || 'Kargo' }}</span>
                <button
                  v-if="o.cargo.trackingNo"
                  type="button"
                  class="font-mono font-semibold text-n-slate-12 bg-n-alpha-black2 rounded px-1.5 py-0.5 hover:bg-n-alpha-2"
                  title="Takip numarasını kopyala"
                  @click="copyTracking(o)"
                >
                  {{ o.cargo.trackingNo }}
                  {{ copiedId === o.id ? '✓ kopyalandı' : '📋' }}
                </button>
                <a
                  v-if="o.cargo.trackingUrl"
                  :href="o.cargo.trackingUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-woot-500 font-semibold hover:underline"
                >
                  Kargo Takip ↗
                </a>
              </div>

              <div v-if="o.address?.full || o.address?.city">
                📍 {{ o.address.name ? o.address.name + ' — ' : ''
                }}{{ o.address.full || '' }}
                <b>{{
                  [o.address.district, o.address.city]
                    .filter(Boolean)
                    .join(' / ')
                }}</b>
              </div>

              <div v-if="o.note">📝 {{ o.note }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
